import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import SitePage, { CallToAction, calendly, styles, T, ACCENT } from '../components/site/SitePage';

const PATH = '/marge-par-canal';
const RED = '#C2410C';

const SIGNS = [
  ['Trois outils, trois marges pour le même mois.', "Seller Central, Shopify et la comptabilité ne comptent pas les mêmes frais. Chaque réunion commence par savoir quel chiffre est le bon."],
  ['Les frais Amazon découverts au virement.', 'Stockage longue durée, retours, remboursements : le versement arrive plus bas que prévu, et personne ne sait sur quels produits.'],
  ['La publicité pilotée au chiffre d\'affaires.', "Une campagne rentable en ventes peut faire perdre de l'argent une fois la commission et la logistique payées."],
  ['Des produits qui perdent de l\'argent sans que personne ne le voie.', 'Noyés dans une marge moyenne correcte, ils consomment du stock, de la trésorerie et du budget publicitaire.']
];

const FEATURES = [
  ['La marge de chaque produit, sur chaque canal', "Amazon, votre site, le B2B : commission, logistique, stockage, publicité, retours et remises, ligne par ligne, sur chaque vente."],
  ['Rapprochée de vos virements et de la compta', "Les versements Amazon et Shopify, vos factures fournisseurs et votre comptabilité donnent le même chiffre, au centime."],
  ['Les alertes qui comptent', "Les produits qui vendent à perte, la publicité qui coûte plus qu'elle ne rapporte, les frais qui dérivent d'un mois sur l'autre."],
  ['Une vue mensuelle pour la direction', 'Une marge par canal, par gamme et par mois, que tout le monde lit de la même façon : finance, achats et marketing.']
];

const FAQ = [
  ["J'ai déjà Sellerboard ou un outil de profit Amazon. Pourquoi un autre outil ?", "Gardez-le pour le suivi au jour le jour d'Amazon. Ce qu'il ne fait pas : votre site et le B2B dans le même calcul, vos propres règles de coût, et le rapprochement avec la comptabilité. C'est cette couche-là que je construis, et je la relie à vos outils existants."],
  ['De quelles données avez-vous besoin ?', "Amazon et Shopify se connectent directement : ventes, frais, remboursements et versements arrivent seuls. Pour le reste (coûts d'achat, factures fournisseurs, compta), je me branche sur votre logiciel quand c'est possible, sinon je reprends vos Excel."],
  ['Pourquoi ma marge Amazon ne colle-t-elle jamais à la compta ?', "Parce qu'elles ne comptent pas les mêmes choses au même moment : Amazon raisonne par commande, la compta par versement et par facture. Les frais de stockage, les remboursements et les écarts de TVA tombent entre les deux. L'outil les range au bon endroit, une fois pour toutes."],
  ['Combien de temps avant un outil utilisable ?', 'Trois semaines pour un premier outil en ligne, sur vos vraies données, validé par votre équipe. Le prix est fixé par écrit avant de commencer.']
];

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

// Aperçu de l'écran de marge, données fictives : mêmes produits et mêmes taux que la démo de
// l'accueil et que le calculateur de marge (Veste Altitude : 34,5 % sur Amazon, 41,0 % sur le site).
const ROWS = [
  ['Veste Altitude', 'Amazon', '18 420', '5 281', '1 768', '6 363', 34.5],
  ['Veste Altitude', 'Site', '9 310', '1 276', '1 341', '3 818', 41.0],
  ['Gants Arête', 'Amazon', '5 240', '2 421', '1 320', '−447', -8.5],
  ['Bonnet Névé', 'Revendeurs', '7 450', '960', '0', '2 608', 35.0],
  ['Bonnet Névé', 'Amazon', '5 120', '2 167', '1 106', '500', 9.8]
];

const Preview = () => (
  <figure style={{ margin: 0, background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 24px 48px -32px rgba(21,23,26,.45)' }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${T.line}`, background: '#F7F8FA' }}>
      <span style={{ fontWeight: 700, fontSize: 14 }}>Marge par produit et canal · septembre</span>
      <span style={{ fontFamily: T.mono, fontSize: 12, color: T.faint }}>Données de démonstration</span>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ fontFamily: T.mono, fontSize: 12, color: T.faint, textAlign: 'right' }}>
            <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'left' }}>Produit</th>
            <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'left' }}>Canal</th>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>CA HT</th>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>Frais</th>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>Pub</th>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>Marge</th>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>%</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([name, canal, ca, frais, pub, marge, pct]) => (
            <tr key={name + canal} style={{ borderTop: `1px solid ${T.line}`, textAlign: 'right', fontFamily: T.mono }}>
              <td style={{ padding: '10px 14px', fontWeight: 600, textAlign: 'left', fontFamily: T.sans }}>{name}</td>
              <td style={{ padding: '10px 14px', textAlign: 'left', fontFamily: T.sans }}>{canal}</td>
              <td style={{ padding: '10px 14px' }}>{ca} €</td>
              <td style={{ padding: '10px 14px' }}>{frais} €</td>
              <td style={{ padding: '10px 14px' }}>{pub} €</td>
              <td style={{ padding: '10px 14px', fontWeight: 600, color: pct < 0 ? RED : T.ink }}>{marge} €</td>
              <td style={{ padding: '10px 14px' }}><span style={{ display: 'inline-block', padding: '2px 8px', border: `1px solid ${pct < 0 ? RED : pct < 15 ? '#B45309' : '#0F766E'}`, color: pct < 0 ? RED : pct < 15 ? '#B45309' : '#0F766E', borderRadius: 3, fontSize: 12 }}>{String(pct).replace('.', ',')} %</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <figcaption style={{ padding: '10px 14px', borderTop: `1px solid ${T.line}`, fontSize: 13, color: T.muted }}>Les gants perdent de l'argent sur Amazon : des retours parce qu'ils taillent petit, et une publicité trop chère pour leur marge.</figcaption>
  </figure>
);

const MargePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SitePage>
      <SEO
        title="Marge réelle par produit et par canal"
        description="Un outil sur mesure qui calcule la marge de chaque produit sur Amazon, votre site et le B2B, frais et pub déduits, rapprochée de la compta. Prix fixe."
        path={PATH}
        breadcrumb={[['Marge par canal', PATH]]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      <section style={{ ...styles.container, padding: '56px 24px 0', display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center' }}>
        <div style={{ flex: '1 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <p style={styles.kicker}>Finance et direction</p>
          <h1 style={styles.h1}>Marge réelle par produit et par canal : un seul chiffre qui fait foi</h1>
          <p style={styles.lead}>Un outil qui calcule ce que chaque produit vous rapporte vraiment sur Amazon, sur votre site et en B2B, une fois la commission, la logistique, la publicité et les retours payés. Le même chiffre pour la finance, les achats et le marketing.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <a href={calendly('marge')} target="_blank" rel="noopener" className="lift" style={styles.button}>Réserver un appel de 30 min</a>
            <Link to="/outils/calcul-marge-amazon" className="lift" style={{ ...styles.button, background: '#FFFFFF', color: T.ink, border: `1px solid ${T.ink}`, fontWeight: 600 }}>Essayer le calculateur de marge</Link>
          </div>
        </div>
        <div style={{ flex: '1 1 520px', minWidth: 0 }}>
          <Preview />
        </div>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Les signes que votre marge vous échappe</h2>
        <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: 1, background: T.line, border: `1px solid ${T.line}` }}>
          {SIGNS.map(([title, text]) => (
            <div key={title} style={{ padding: 22, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <p style={{ margin: 0, fontWeight: 700 }}>{title}</p>
              <p style={{ margin: 0, fontSize: 15, color: T.muted }}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Ce que fait l'outil</h2>
        <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 28 }}>
          {FEATURES.map(([title, text], i) => (
            <div key={title} style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: `2px solid ${ACCENT}`, paddingTop: 14 }}>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: ACCENT }}>0{i + 1}</span>
              <p style={{ margin: 0, fontSize: 19, fontWeight: 800, fontStretch: '88%', lineHeight: 1.2 }}>{title}</p>
              <p style={{ margin: 0, fontSize: 16, color: T.text }}>{text}</p>
            </div>
          ))}
        </div>
        <p style={{ margin: '28px 0 0', fontSize: 17, color: T.text, maxWidth: 760 }}>Pour un premier aperçu sur un produit : <Link to="/outils/calcul-marge-amazon">calculez sa marge sur Amazon et sur votre site</Link>. L'outil fait la même chose sur tout le catalogue, chaque mois, avec les vrais frais. <a href="/#equipes">Voir la démo</a>.</p>
      </section>

      <section style={{ ...styles.container, paddingTop: 88, display: 'flex', flexWrap: 'wrap', gap: 32 }}>
        <div style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2 style={styles.h2}>Un premier outil en 3 semaines, à prix fixe</h2>
          <p style={{ margin: 0, fontSize: 18, color: T.text }}>On part de vos exports et de vos Excel tels qu'ils sont, Amazon et Shopify se connectent directement. Je range tout dans une seule base, puis je construis l'outil dessus, testé par votre équipe sur vos vraies données. Le code vous appartient.</p>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 0, background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint }}>Outil de marge sur mesure</span>
          <span style={{ fontSize: 40, fontWeight: 800, fontStretch: '80%' }}><span style={{ fontSize: 16, color: T.muted, fontWeight: 600 }}>dès</span> 2 900 € <span style={{ fontSize: 16, color: T.muted, fontWeight: 600 }}>HT</span></span>
          <span style={{ fontSize: 15, color: T.text }}>En 3 semaines, la marge de chaque produit sur vos canaux principaux. Le rapprochement avec la compta et les alertes s'ajoutent ensuite, sur la même base. Tarif de lancement : 2 030 € HT au lieu de 2 900 € pour un premier outil, si vous acceptez qu'il fasse l'objet d'une étude de cas.</span>
          <a href="/#offres" style={{ fontSize: 15 }}>Voir toutes les offres</a>
        </div>
      </section>

      <section style={{ ...styles.narrow, paddingTop: 88 }}>
        <h2 style={styles.h2}>Questions fréquentes</h2>
        <div style={{ marginTop: 20, borderTop: `1px solid ${T.line2}` }}>
          {FAQ.map(([q, a]) => (
            <details key={q} style={{ borderBottom: `1px solid ${T.line2}`, padding: '16px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>{q}</summary>
              <p style={{ margin: '10px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <CallToAction from="marge-bas" title="Parlons de votre marge" text="30 minutes pour voir ce que l'outil changerait chez vous, puis un prix fixe par écrit sous 3 jours." />
    </SitePage>
  );
};

export default MargePage;
